import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { UiButton, UiRangePicker, UiTab, UiTable } from "../../../components";
import { showErrorNotification } from "../../../components/uiNotification";
import { downloadReport } from "../../../assets/images";
import { GST_INVOICE_TABS, PAYMENT_CATEGORY_BY_TAB, PAYMENT_DESCRIPTION, getGstInvoiceColumns, TDS_COLUMNS } from "./config";
import GstInvoiceReport from "./report";
import SelfTaxReport from "./report/selfTaxReport";
import SalesTaxReport from "./report/salesTaxReport";
import RefundReceiptReport from "./report/refundReceiptReport";
import TdsReport from "./report/tdsReport";
import { TRANSACTIONMANAGEMENT } from "../../../apiServices/endpoints";
import * as api from "../../../actions";
import { loaderReducer } from "../../../reducer/loader.reducer";
import { formatAmount, tablePageSizeOptions } from "../../../utils/appUtils";
import "./style.scss";
import { useSelector } from 'react-redux';

const GstInvoice = () => {
    const dispatch = useDispatch();
    const reportRef = useRef(null);
    const [filter, setFilter] = useState({
        activeTab: "SELF_TAX_INVOICES",
        activeTabKey: "1",
        page: 0,
        size: 10,
        month: dayjs(),
        monthString: dayjs().format("YYYY-MM"),
        fromDate: "",
        toDate: "",
        isReportOpen: false
    });
    const [isActionDownloadLoading, setIsActionDownloadLoading] = useState(false);
    const [printMode, setPrintMode] = useState("SUMMARY");
    const [isPrintRequested, setIsPrintRequested] = useState(false);
    const [invoiceDetailReportData, setInvoiceDetailReportData] = useState(null);
    const reportPageSize = filter?.size === -1 ? -1 : filter?.size > 0 ? filter?.size : 10;
    const currentPageSize = filter?.size > 0 ? filter?.size : 10;
    const configResults = useSelector((state) => state?.configAction?.configData);
    const sacNumber = configResults?.find((detail) => detail?.configId === "SACNumber")?.configuration?.[0];

    const isTdsTab = String(filter?.activeTabKey) === "4";

    const getInvoiceReportList = (paymentCategory, page, size, monthString, fromDate, toDate, isTds) => {
        if (isTds) {
            return api.get(`${TRANSACTIONMANAGEMENT}/invoices_report?page=${page}&size=${size === -1 ? -1 : size > 0 ? size : 10}&isTds=true${fromDate && toDate ? `&startDate=${fromDate}&endDate=${toDate}` : monthString ? `&startDate=${dayjs(`${monthString}-01`).format("YYYY-MM-DD")}&endDate=${dayjs(`${monthString}-01`).endOf("month").format("YYYY-MM-DD")}` : ""}`);
        }
        return api.get(`${TRANSACTIONMANAGEMENT}/invoices_report?paymentCategory=${paymentCategory}&page=${page}&size=${size === -1 ? -1 : size > 0 ? size : 10}${fromDate && toDate ? `&startDate=${fromDate}&endDate=${toDate}` : monthString ? `&startDate=${dayjs(`${monthString}-01`).format("YYYY-MM-DD")}&endDate=${dayjs(`${monthString}-01`).endOf("month").format("YYYY-MM-DD")}` : ""}`);
    };

    const { data: invoiceResponse, isFetching } = useQuery(
        ["FETCH_GST_INVOICE_REPORT", filter?.activeTabKey, filter?.monthString, filter?.fromDate, filter?.toDate, filter?.page, reportPageSize],
        () => getInvoiceReportList(
            PAYMENT_CATEGORY_BY_TAB?.[filter?.activeTabKey] || PAYMENT_CATEGORY_BY_TAB?.["1"],
            filter?.page || 0,
            reportPageSize,
            filter?.monthString,
            filter?.fromDate,
            filter?.toDate,
            isTdsTab
        ),
        {
            refetchOnWindowFocus: false
        }
    );

    const invoiceReportResponse = invoiceResponse?.result?.InvoiceResponse || invoiceResponse?.result?.invoiceResponse || invoiceResponse?.result || {};
    const filteredDataSource = (invoiceReportResponse?.invoice?.content || [])?.map((invoice, index) => {
        const lawyerName = `${invoice?.lawyerResponse?.firstName || ""} ${invoice?.lawyerResponse?.middleName || ""} ${invoice?.lawyerResponse?.lastName || ""}`?.trim();
        const customerName = `${invoice?.customerResponse?.firstName || ""} ${invoice?.customerResponse?.middleName || ""} ${invoice?.customerResponse?.lastName || ""}`?.trim();
        const supplierOfService = filter?.activeTabKey === "1"
            ? lawyerName || "Nil"
            : filter?.activeTabKey === "2" || filter?.activeTabKey === "3"
                ? customerName || "Nil"
                : lawyerName || customerName || "Nil";
        const paymentCategory = invoice?.paymentCategory || "";
        const taxableValue = Number(invoice?.taxableValue ?? invoice?.amount ?? invoice?.finalAmount ?? 0) || 0;
        const gstValue = Number(invoice?.gstAmount ?? 0) || 0;
        const totalInvoiceValue = Number(invoice?.totalInvoiceValue ?? invoice?.finalAmount ?? invoice?.amount ?? 0) || 0;

        return {
            id: invoice?.transactionId || invoice?.taxInvoiceNo || invoice?.advanceInvoiceNo || `${index}`,
            transactionId: invoice?.transactionId || "",
            date: invoice?.date || invoice?.taxInvoiceDate || invoice?.transactionDate || "",
            invoiceNumber: invoice?.taxInvoiceNo || invoice?.advanceInvoiceNo || "",
            supplierOfService,
            description: PAYMENT_DESCRIPTION?.[paymentCategory] || paymentCategory || "Invoice",
            serviceCount: 1,
            taxableValue,
            gstValue,
            gstAmount: gstValue,
            totalInvoiceValue
        };
    }) || [];

    const tdsDataSource = (invoiceReportResponse?.invoice?.content || invoiceReportResponse?.tdsList || invoiceReportResponse?.content || [])?.map((item, index) => {
        const lawyerObj = item?.lawyerResponse || item?.advocateResponse || item?.lawyer || {};
        const advocateId = lawyerObj?.lawyerNo || item?.lawyerNo || lawyerObj?.advocateId || item?.advocateId || item?.lawyerId || "Nil";
        const name = lawyerObj?.firstName || lawyerObj?.lastName
            ? [lawyerObj?.salutation, lawyerObj?.firstName, lawyerObj?.middleName, lawyerObj?.lastName].filter(Boolean).join(" ")
            : (item?.advocateName || item?.lawyerName || item?.name || "Nil");
        const panNum = lawyerObj?.panCardNumber || lawyerObj?.panCard || lawyerObj?.panNumber || item?.panCardNumber || item?.panCard || item?.panNumber || item?.panNum || item?.pan || "Nil";
        const mobileNo = lawyerObj?.mobileNumber || item?.mobileNumber || "Nil";
        const emailId = lawyerObj?.email || item?.email || "Nil";
        const earnedAmountAboveTds = item?.lawyerFee ?? item?.earnedAmountAboveTds ?? item?.earnedAmount ?? item?.amountAboveTds ?? item?.taxableValue ?? item?.amount ?? 0;
        const tdsAmount = item?.tdsAmount ?? item?.tds ?? item?.gstAmount ?? 0;

        return {
            id: item?.id || item?.transactionId || item?.lawyerId || lawyerObj?.lawyerId || `${index}`,
            key: `${index}`,
            lawyerNo: advocateId,
            name,
            panNum,
            mobileNumber: mobileNo,
            email: emailId,
            earnedAmountAboveTds,
            tdsAmount
        };
    }) || [];

    const totalTdsCount = Number(invoiceReportResponse?.totalTdsLawyerCount ?? invoiceReportResponse?.totalElements ?? invoiceReportResponse?.totalCount ?? invoiceReportResponse?.invoice?.totalElements ?? tdsDataSource?.length) || 0;
    const totalTdsAmount = Number(invoiceReportResponse?.totalTdsValue ?? invoiceReportResponse?.totalTdsAmount ?? invoiceReportResponse?.tdsAmount ?? tdsDataSource?.reduce((sum, item) => sum + (Number(item?.tdsAmount) || 0), 0)) || 0;

    const tdsSummaryItems = [
        { label: "Advocate Count", value: totalTdsCount },
        { label: "Total TDS Amount", value: `₹ ${formatAmount(totalTdsAmount)}` }
    ];

    const totalElements = isTdsTab
        ? (Number(invoiceReportResponse?.invoice?.totalElements ?? invoiceReportResponse?.totalTdsLawyerCount ?? tdsDataSource?.length) || 0)
        : Number(invoiceReportResponse?.invoice?.totalElements ?? invoiceReportResponse?.totalInvCount ?? filteredDataSource?.length) || 0;
    const totalInvoiceCount = Number(invoiceReportResponse?.totalInvCount ?? filteredDataSource?.length) || 0;
    const totalTaxableValue = Number(invoiceReportResponse?.totalTaxableValue) || 0;
    const totalGstValue = Number(invoiceReportResponse?.totalGstValue) || 0;
    const totalInvoiceValue = Number(invoiceReportResponse?.totalInvValue) || 0;
    const reportTitle = filter?.activeTabKey === "1" ? "SELF TAX INVOICE REPORTS" : filter?.activeTabKey === "2" ? "SALES TAX INVOICE REPORTS" : "REFUND RECEIPTS REPORTS";
    const invoiceNoLabel = filter?.activeTabKey === "1" ? "Self Tax INV No" : filter?.activeTabKey === "2" ? "Invoice To" : "Receipt No";
    const supplierOfServiceLabel = filter?.activeTabKey === "3" ? "Receipt To" : "Supplier of Service";
    const detailReportByTab = {
        "1": <SelfTaxReport reportData={invoiceDetailReportData} sacNumber={sacNumber} />,
        "2": <SalesTaxReport reportData={invoiceDetailReportData} sacNumber={sacNumber} />,
        "3": <RefundReceiptReport reportData={invoiceDetailReportData} sacNumber={sacNumber} />
    };

    const summaryItems = [
        { label: "Total INV Count", value: formatAmount(totalInvoiceCount) },
        { label: "Total Taxable Value", value: formatAmount(totalTaxableValue) },
        { label: "Total GST Value", value: formatAmount(totalGstValue) },
        { label: "Total INV Value", value: formatAmount(totalInvoiceValue) }
    ];

    const handleActionDownload = async (invoiceDetail) => {
        if (!invoiceDetail?.transactionId) {
            showErrorNotification("Error", "Transaction ID not found");
            return;
        }

        setIsActionDownloadLoading(true);
        try {
            const invoiceDetailResponse = await api.get(`${TRANSACTIONMANAGEMENT}/getInvoice/${invoiceDetail?.transactionId}`);
            const detailReportData = invoiceDetailResponse?.result?.InvoiceResponse || invoiceDetailResponse?.result?.invoiceResponse || {};
            setInvoiceDetailReportData(detailReportData);
            setPrintMode("DETAIL");
            setIsPrintRequested(true);
            setFilter((previousFilter) => ({ ...previousFilter, isReportOpen: true }));
        } catch (error) {
            showErrorNotification("Error", error?.response?.data?.error?.message || error?.message || "Failed to fetch invoice details");
        } finally {
            setIsActionDownloadLoading(false);
        }
    };

    const invoiceColumns = getGstInvoiceColumns({
        onActionDownload: handleActionDownload,
        activeTabKey: filter?.activeTabKey
    });
    const tableColumns = isTdsTab ? TDS_COLUMNS : invoiceColumns;
    const dataSource = isTdsTab ? tdsDataSource : filteredDataSource;
    const printPageStyle = `@page {
                size: ${printMode === "DETAIL" ? "A4 portrait" : "A4 landscape"} !important;
                margin: ${printMode === "DETAIL" ? "6mm" : "8mm"} !important;
            }
            @media print {
                @page {
                    size: ${printMode === "DETAIL" ? "A4 portrait" : "A4 landscape"} !important;
                    margin: ${printMode === "DETAIL" ? "6mm" : "8mm"} !important;
                }
            }
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }`;

    const handleInvoicePrint = useReactToPrint({
        content: () => reportRef.current,
        onAfterPrint: () => {
            setFilter((previousFilter) => ({ ...previousFilter, isReportOpen: false, size: previousFilter?.size === -1 ? 10 : previousFilter?.size }));
            setIsPrintRequested(false);
            if (printMode === "DETAIL") {
                setPrintMode("SUMMARY");
            }
        },
        pageStyle: printPageStyle
    });

    const handleReportPrint = () => {
        setPrintMode("SUMMARY");
        setInvoiceDetailReportData(null);
        setIsPrintRequested(true);
        setFilter((previousFilter) => ({ ...previousFilter, size: -1, isReportOpen: true }));
    };

    const handleMonthChange = (month) => {
        if (month) {
            setFilter((previousFilter) => ({
                ...previousFilter,
                month,
                monthString: dayjs(month).format("YYYY-MM"),
                page: 0,
                fromDate: "",
                toDate: ""
            }));
            return;
        }

        setFilter((previousFilter) => ({
            ...previousFilter,
            month: null,
            monthString: "",
            page: 0
        }));
    };

    const handleDateChange = (date) => {
        if (date?.[0] && date?.[1]) {
            setFilter((previousFilter) => ({
                ...previousFilter,
                fromDate: dayjs(date?.[0]).format("YYYY-MM-DD"),
                toDate: dayjs(date?.[1]).format("YYYY-MM-DD"),
                page: 0,
                month: null,
                monthString: ""
            }));
            return;
        }

        setFilter((previousFilter) => ({
            ...previousFilter,
            fromDate: "",
            toDate: "",
            page: 0
        }));
    };

    const handleTabChange = (tabKey) => {
        GST_INVOICE_TABS?.forEach((tab) => {
            if (String(tab?.key) === String(tabKey)) {
                setFilter((previousFilter) => ({
                    ...previousFilter,
                    activeTab: tab?.content || previousFilter?.activeTab,
                    activeTabKey: String(tabKey),
                    page: 0,
                    size: 10,
                    month: previousFilter?.month || dayjs(),
                    monthString: previousFilter?.monthString || dayjs().format("YYYY-MM"),
                    fromDate: "",
                    toDate: "",
                    isReportOpen: false
                }));
            }
        });
    };

    const handlePagination = (page, size) => {
        setFilter((previousFilter) => ({
            ...previousFilter,
            page: page - 1,
            size: size > 0 ? size : 10
        }));
    };

    useEffect(() => {
        dispatch(loaderReducer(isFetching || isActionDownloadLoading));
    }, [dispatch, isFetching, isActionDownloadLoading]);

    useEffect(() => {
        if (filter?.isReportOpen && isPrintRequested && !isFetching && !isActionDownloadLoading) {
            handleInvoicePrint();
        }
    }, [filter?.isReportOpen, isFetching, handleInvoicePrint, isPrintRequested, isActionDownloadLoading]);

    return (
        <div className="gst-invoice-page">
            <div className="gst-invoice-filter-row">
                <DatePicker
                    picker="month"
                    format="MMM"
                    value={filter?.month}
                    style={{ width: 140, borderRadius: 6 }}
                    onChange={(month) => handleMonthChange(month)}
                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                />
                {!isTdsTab && (
                    <UiRangePicker
                        value={filter?.fromDate && filter?.toDate ? [dayjs(filter?.fromDate), dayjs(filter?.toDate)] : null}
                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                        onChange={(date) => handleDateChange(date)}
                    />
                )}
                <UiButton type="primary" onClick={handleReportPrint}>
                    <img src={downloadReport} alt="Download" />
                    Download Report
                </UiButton>
            </div>
            <UiTab
                tabs={GST_INVOICE_TABS}
                activeTabKey={filter?.activeTabKey}
                onChange={handleTabChange}
                noBadge
            />
            {isTdsTab ? (
                <div className="invoice-summary-strip" style={{ gridTemplateColumns: "repeat(2, minmax(180px, 1fr))" }}>
                    {
                        tdsSummaryItems?.map((item) => {
                            return <div key={item?.label} className="invoice-summary-item">
                                <p className="invoice-summary-label">{item?.label}</p>
                                <h4 className="invoice-summary-value">{item?.value}</h4>
                            </div>;
                        })
                    }
                </div>
            ) : (
                <div className="invoice-summary-strip">
                    {
                        summaryItems?.map((item) => {
                            return <div key={item?.label} className="invoice-summary-item">
                                <p className="invoice-summary-label">{item?.label}</p>
                                <h4 className="invoice-summary-value">{item?.value}</h4>
                            </div>;
                        })
                    }
                </div>
            )}
            <UiTable
                columns={tableColumns}
                dataSource={dataSource}
                rowKey={(detail) => detail?.id || detail?.customerId || detail?.customerNo}
                pagination={filter?.isReportOpen || (totalElements || 0) <= currentPageSize ? false : {
                    total: totalElements,
                    pageSize: currentPageSize,
                    current: (filter?.page || 0) + 1,
                    pageSizeOptions: tablePageSizeOptions,
                    showSizeChanger: true,
                    onChange: (page, size) => handlePagination(page, size)
                }}
                scroll={{ x: "max-content", y: "calc(100vh - 365px)" }}
            />
            <div style={{ display: "none" }}>
                <div ref={reportRef}>
                    {
                        printMode === "DETAIL"
                            ? detailReportByTab?.[filter?.activeTabKey] || <SelfTaxReport reportData={invoiceDetailReportData} sacNumber={sacNumber} />
                            : isTdsTab
                                ? <TdsReport
                                    data={tdsDataSource}
                                    reportTitle="TDS AMOUNT REPORTS"
                                    summary={{
                                        totalTdsCount,
                                        totalTdsAmount
                                    }}
                                />
                                : <GstInvoiceReport
                                    data={filteredDataSource}
                                    reportTitle={reportTitle}
                                    invoiceNoLabel={invoiceNoLabel}
                                    supplierOfServiceLabel={supplierOfServiceLabel}
                                    summary={{
                                        totalInvoiceCount,
                                        totalTaxableValue,
                                        totalGstValue,
                                        totalInvoiceValue
                                    }}
                                />
                    }
                </div>
            </div>
        </div>
    );
};

export default GstInvoice;
